import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDraggable } from '@dnd-kit/core';
import { GripVertical, Package, Boxes } from 'lucide-react';
import { stockApi } from '@/features/stock/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { projectStockPoolApi, type ProjectPoolRow } from './projectStockPoolApi';
import { CommitToProjectDialog } from './CommitToProjectDialog';
import type { XkStock } from '@/lib/api/types';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { DragData } from './dnd';

/**
 * Horizontal carousel of the brand's xotski catalog. Each chip is a drag
 * source — drop it on an event to allocate it across all that event's
 * stations. PLAN tab, top of the left column.
 */
export function XotskiCarousel({
  brandId,
  groupId,
}: {
  brandId: number;
  /** The project whose pool caps this lane. Omit for brand-wide views. */
  groupId?: number;
}) {
  const t = useT();
  const stockQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId),
    queryFn: () => stockApi.list({ companyId: brandId }),
    enabled: brandId > 0,
  });
  // Brand headroom comes computed from the API (`stock.headroom`), with the
  // very formula the allocation gate applies. This lane used to re-derive it
  // from a brand-wide allocation list that was paginated to 25 rows and
  // summed `quantity` where the gate subtracts `available`: the badge said
  // 1200 free and the drop was refused at 800.

  // Task 7.2 — when the project has adopted the pool, THIS is the warehouse:
  // what the project may draw, not what the brand owns. A project with no rows
  // is uncapped and falls back to brand headroom, exactly as before.
  const poolQ = useQuery({
    queryKey: queryKeys.projectStockPool.byProject(groupId ?? 0),
    queryFn: () => projectStockPoolApi.list(groupId as number),
    enabled: !!groupId && groupId > 0,
  });
  const poolByStock = useMemo(() => {
    const m = new Map<number, ProjectPoolRow>();
    for (const r of poolQ.data ?? []) m.set(r.stockId, r);
    return m;
  }, [poolQ.data]);

  // Which xotski is being committed to the project, if any.
  const [committing, setCommitting] = useState<{ id: number; name: string; brandFree: number | null } | null>(null);

  const items = stockQ.data?.items ?? [];

  return (
    <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3">
      <div className="mb-2 flex items-center gap-2">
        <Package className="h-4 w-4 text-[var(--color-accent-ink)]" />
        {/* Not `uppercase`: the merch term ships as "Xotskies", and CSS-casing
            it rendered a shouted "XOTSKIES" that the vocabulary rules drop. */}
        <h3 className="text-[11px] font-extrabold tracking-wide text-[var(--color-xk-text-muted)]">
          {t('planner.xotskiCarousel.title')}
        </h3>
      </div>
      {items.length === 0 ? (
        <p className="px-1 py-3 text-xs text-[var(--color-xk-text-muted)]">
          {t('planner.xotskiCarousel.empty')}
        </p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {items.map((s) => {
            // The project pool wins once ADOPTED — it is the tighter, more
            // truthful number for this board. A row that is not adopted only
            // carries `allocated` for the commit dialog; the brand still rules.
            const fromProject = poolByStock.get(s.id);
            const brandFree = s.headroom ?? null;
            const available = fromProject?.adopted ? fromProject.remaining : brandFree;
            return (
              <XotskiChip
                key={s.id}
                stock={s}
                available={available}
                fromProjectPool={!!fromProject?.adopted}
                onCommit={
                  groupId
                    ? () => setCommitting({ id: s.id, name: s.name, brandFree })
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      {groupId && committing && (
        <CommitToProjectDialog
          open
          onOpenChange={(v) => !v && setCommitting(null)}
          eventGroupId={groupId}
          stockId={committing.id}
          stockName={committing.name}
          brandFree={committing.brandFree}
          current={poolByStock.get(committing.id)}
        />
      )}
    </div>
  );
}

function XotskiChip({
  stock,
  available,
  fromProjectPool = false,
  onCommit,
}: {
  stock: XkStock;
  available: number | null;
  /** True when the number shown is the project's pool, not brand headroom. */
  fromProjectPool?: boolean;
  /** Opens the commit dialog. Absent on brand-wide views with no project. */
  onCommit?: () => void;
}) {
  const t = useT();
  // `available` is the real allocatable headroom (brand pool − already
  // allocated), matching the backend gate. null → untracked/unlimited (legacy
  // rows) → never disabled. 0 → fully allocated → not draggable.
  const remaining = available;
  const tracked = remaining != null;
  const disabled = tracked && remaining <= 0;

  const data: DragData = {
    type: 'xotski',
    stockId: stock.id,
    name: stock.name,
    image: stock.image,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `xotski-${stock.id}`,
    data,
    disabled,
  });
  return (
    <div className="relative shrink-0">
    <button
      ref={setNodeRef}
      type="button"
      title={
        disabled
          ? t('planner.xotskiCarousel.outOfStock')
          : tracked
            ? t(
                // Say WHOSE number this is: a project pool and brand headroom
                // can differ a lot, and "12 left" is misleading without it.
                fromProjectPool
                  ? 'planner.xotskiCarousel.availableInProject'
                  : 'planner.xotskiCarousel.availableTip',
                { n: String(remaining) },
              )
            : undefined
      }
      {...(disabled ? {} : listeners)}
      {...attributes}
      className={cn(
        'relative flex w-28 shrink-0 flex-col items-center gap-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-base)] p-2 text-center transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-grab hover:border-[var(--color-accent-ink)] active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
    >
      {/* Availability badge — accent when in stock, error when at zero. */}
      {tracked && (
        <span
          className={cn(
            'absolute right-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-black tabular-nums',
            disabled
              ? 'bg-[rgb(239_68_68/0.15)] text-[var(--color-error)]'
              : 'bg-[var(--color-accent-15)] text-[var(--color-accent-ink)]',
          )}
        >
          {remaining}
        </span>
      )}
      {stock.image ? (
        <img
          src={resolveImageUrl(stock.image)}
          alt=""
          className="h-12 w-12 rounded object-cover"
          draggable={false}
        />
      ) : (
        <div className="flex h-12 w-12 items-center justify-center rounded bg-[var(--color-bg-surface)]">
          <Package className="h-5 w-5 text-[var(--color-xk-text-muted)]" />
        </div>
      )}
      <span className="line-clamp-1 w-full text-[11px] font-semibold text-[var(--color-xk-lavender)]">
        {stock.name}
      </span>
      {disabled ? (
        <span className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-error)]">
          {t('planner.xotskiCarousel.out')}
        </span>
      ) : (
        <GripVertical className="h-3 w-3 text-[var(--color-xk-text-muted)]" />
      )}
    </button>
      {/* Commit to the project — a sibling, not a child: the chip itself is the
          draggable button and buttons cannot nest. */}
      {onCommit && (
        <button
          type="button"
          onClick={onCommit}
          title={t('planner.pool.commitTip')}
          aria-label={t('planner.pool.commitTip')}
          className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)] text-[var(--color-fg-muted)] shadow-sm transition-colors hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]"
        >
          <Boxes className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
